'use client';

import { createContext, useContext, useState, type ReactNode } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

import { MODEL_KEYS, type PartnersModelKey } from './partners-types';

/*
 * Prepínač „Model A / Model B" zvýrazní kartu modelu aj jej stĺpec v tabuľke rozdelenia práce
 * (dve rôzne sekcie), preto jeden klientsky kontext pre obe.
 */

const ModelContext = createContext<{
  model: PartnersModelKey;
  setModel: (model: PartnersModelKey) => void;
}>({ model: 'a', setModel: () => undefined });

export function PartnersModelProvider({ children }: { children: ReactNode }) {
  const [model, setModel] = useState<PartnersModelKey>('a');
  return <ModelContext.Provider value={{ model, setModel }}>{children}</ModelContext.Provider>;
}

export function ModelSwitch({ label, names }: { label: string; names: string[] }) {
  const { model, setModel } = useContext(ModelContext);
  return (
    <div
      role="group"
      aria-label={label}
      className="mb-7 flex max-w-full border-2 border-solid border-[#161412] min-[521px]:inline-flex"
    >
      {names.slice(0, MODEL_KEYS.length).map((name, index) => (
        <button
          key={MODEL_KEYS[index]}
          type="button"
          aria-pressed={model === MODEL_KEYS[index]}
          onClick={() => setModel(MODEL_KEYS[index])}
          className="flex-1 cursor-pointer appearance-none border-0 bg-transparent px-[10px] py-3 text-[14px] font-semibold text-[#161412] aria-pressed:bg-[#161412] aria-pressed:text-white min-[521px]:flex-initial min-[521px]:px-[22px] min-[521px]:py-[14px] min-[521px]:text-[15px]"
        >
          {name}
        </button>
      ))}
    </div>
  );
}

export function ModelCard({ model, children }: { model: PartnersModelKey; children: ReactNode }) {
  const active = useContext(ModelContext).model;
  return (
    <article
      className={cn(
        'flex flex-col border border-solid border-[#161412]/10 bg-white [transition:border-color_.2s,box-shadow_.2s,opacity_.2s]',
        model === active && 'border-primary shadow-[0_0_0_1px_hsl(var(--primary))]',
        model !== active && 'opacity-[.45]',
      )}
    >
      {children}
    </article>
  );
}

export function SplitCell({
  model,
  className,
  children,
}: {
  model: PartnersModelKey;
  className?: string;
  children: ReactNode;
}) {
  const active = useContext(ModelContext).model;
  return <td className={cn(className, model === active && 'bg-[hsl(36_57%_94%)]')}>{children}</td>;
}
